
import { defineComponent } from "vue";
import dayjs from "dayjs";
import { getRecentActivity } from "@/api/analytics";
import DashboardTabNavigator from "@/components/dashboard/DashboardTabNavigator.vue";

const FETCH_LIMIT = 100;
const VISIBLE_ROWS = 3;

interface ActivityRow {
  id?: string;
  name: string;
  type: string;
  date: string;
  amount: string;
  amountAlt: string;
  status: string;
  method: string;
  methodAlt: string;
  direction: "in" | "out";
}

interface ActivityApiItem {
  id?: string;
  userId?: string;
  activityType?: string;
  title?: string;
  details?: string | null;
  amount?: number;
  metadata?: Record<string, unknown>;
  isRead?: boolean;
  createdAt?: string;
}

export default defineComponent({
  name: "RecentActivityCard",
  components: {
    DashboardTabNavigator,
  },
  props: {
    currencySymbol: { type: String, default: "" }
  },
  data() {
    return {
      loading: true,
      rows: [] as ActivityRow[],
      scrollSpeed: 0.3,
      animationFrameId: null as number | null,
      isPaused: false,
      viewportHeight: 220
    };
  },
  computed: {
    loopRows(): ActivityRow[] {
      if (this.rows.length > VISIBLE_ROWS) {
        return [...this.rows, ...this.rows];
      }
      return this.rows;
    },
    viewportStyle(): Record<string, string> {
      return {
        "--recent-activity-height": `${this.viewportHeight}px`
      };
    }
  },
  methods: {
    badgeClass(status: string): string {
      const s = (status || "").toLowerCase();
      if (s === "success") return "seq-badge--success";
      if (s === "pending") return "seq-badge--pending";
      return "seq-badge--danger";
    },
    formatActivityType(type: string): string {
      if (!type) return "Activity";
      return type
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    formatAmount(amount: number): string {
      const num = Number(amount) || 0;
      const prefix = num < 0 ? "- " : "";
      const formatted = Math.abs(num).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      const symbol = this.currencySymbol;
      return `${prefix}${symbol}${symbol ? " " : ""}${formatted}`;
    },
    formatActivityDate(dateString: string): string {
      const date = dayjs(dateString);
      if (!date.isValid()) return "";
      return date.format("MMM. D, YYYY hh:mm A");
    },
    resolveStatus(activity: ActivityApiItem): string {
      const metadata = activity.metadata || {};
      const raw = String(
        metadata.status || metadata.payment_status || metadata.state || ""
      ).toLowerCase();

      if (raw.includes("pending")) return "Pending";
      if (raw.includes("fail") || raw.includes("error") || raw.includes("declin")) {
        return "Failed";
      }
      return "Success";
    },
    resolveMethod(activity: ActivityApiItem): string {
      const metadata = activity.metadata || {};
      const method = metadata.method || metadata.payment_method || metadata.channel;
      if (method != null && String(method).trim() !== "") {
        return String(method);
      }
      return this.formatActivityType(activity.activityType || "");
    },
    resolveMethodAlt(activity: ActivityApiItem): string {
      const metadata = activity.metadata || {};
      const reference = metadata.reference_number || metadata.referenceNumber;
      if (reference != null && String(reference).trim() !== "") {
        return String(reference);
      }
      return activity.userId || "";
    },
    mapActivity(activity: ActivityApiItem): ActivityRow {
      const amount = Number(activity.amount) || 0;

      return {
        id: activity.id,
        name: activity.title || this.formatActivityType(activity.activityType || ""),
        type: this.formatActivityType(activity.activityType || ""),
        date: activity.createdAt
          ? this.formatActivityDate(activity.createdAt)
          : "",
        amount: this.formatAmount(amount),
        amountAlt: activity.details || activity.userId || "",
        status: this.resolveStatus(activity),
        method: this.resolveMethod(activity),
        methodAlt: this.resolveMethodAlt(activity),
        direction: amount < 0 ? "out" : "in"
      };
    },
    measureViewport() {
      const viewport = this.$refs.scrollViewport as HTMLElement | undefined;
      if (!viewport) return;

      const firstRow = viewport.querySelector("tbody tr") as HTMLElement | null;
      if (firstRow) {
        const rowHeight = firstRow.getBoundingClientRect().height;
        this.viewportHeight = Math.ceil(rowHeight * VISIBLE_ROWS);
      }
    },
    startAutoScroll() {
      this.stopAutoScroll();
      if (this.rows.length <= VISIBLE_ROWS) return;

      const tick = () => {
        if (!this.isPaused) {
          const viewport = this.$refs.scrollViewport as HTMLElement | undefined;
          if (viewport) {
            viewport.scrollTop += this.scrollSpeed;
            const loopHeight = viewport.scrollHeight / 2;
            if (loopHeight > 0 && viewport.scrollTop >= loopHeight) {
              viewport.scrollTop -= loopHeight;
            }
          }
        }
        this.animationFrameId = requestAnimationFrame(tick);
      };

      this.animationFrameId = requestAnimationFrame(tick);
    },
    stopAutoScroll() {
      if (this.animationFrameId != null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },
    pauseAutoScroll() {
      this.isPaused = true;
    },
    resumeAutoScroll() {
      this.isPaused = false;
    },
    async fetchRecentActivity() {
      this.stopAutoScroll();
      this.loading = true;

      try {
        const response = await getRecentActivity({ limit: FETCH_LIMIT });
        const activities = Array.isArray(response.data?.activities)
          ? response.data.activities
          : [];

        this.rows = activities.map((activity: ActivityApiItem) =>
          this.mapActivity(activity)
        );
      } catch (error) {
        this.rows = [];
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.measureViewport();
          this.startAutoScroll();
        });
      }
    }
  },
  mounted() {
    this.fetchRecentActivity();
  },
  beforeUnmount() {
    this.stopAutoScroll();
  }
});
